import React from "react";
import { motion } from "framer-motion";
import { Shield, Lock, Database, Globe, Mail } from "lucide-react";
import { Link } from "react-router-dom";

import {
  TechLabel,
  StatusBadge,
  PageContainer,
  motionFadeUp,
  C,
} from "../../design-system";
import { createPageUrl } from "../../utils";

const sections = [
  {
    icon: Database,
    title: "Information we collect",
    body: [
      "Account details you provide (name, work email, company) and the content you choose to upload, résumés, job descriptions, candidate notes.",
      "Usage telemetry needed to keep the service working: page views, feature interactions, error reports. We do not sell this data.",
      "Cookies for authentication and remembering your session. No advertising trackers.",
    ],
  },
  {
    icon: Lock,
    title: "How we use it",
    body: [
      "To run the platform, deliver AI features (parsing, scoring, drafting) and personalize results to your team's data.",
      "To detect abuse and keep your account secure.",
      "To send service notices, billing receipts and product updates you can opt out of.",
    ],
  },
  {
    icon: Globe,
    title: "Where it lives",
    body: [
      "Customer data is stored in encrypted form, in regions you select where multi-region deployments are available.",
      "We work with vetted sub-processors (cloud hosting, email, payments). The current list is available on request.",
      "International transfers happen under standard contractual clauses where required.",
    ],
  },
  {
    icon: Shield,
    title: "Your rights",
    body: [
      "Request a copy of your data, ask us to correct it, or delete it, at any time.",
      "Export raw candidate / employee data via the export tools or by emailing us.",
      "Withdraw consent for any processing that relies on consent.",
    ],
  },
];

export default function Privacy() {
  return (
    <div
      className="relative"
      style={{
        background: C.paper,
        color: C.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* HERO */}
      <section
        className="relative pt-16 pb-16 md:pt-24 md:pb-20 border-b ds-bg-spotlight"
        style={{ borderColor: C.hairline }}
      >
        <PageContainer>
          <motion.div initial="hidden" animate="show" variants={motionFadeUp}>
            <motion.div variants={motionFadeUp} custom={0}>
              <StatusBadge>Privacy · last updated June 2026</StatusBadge>
            </motion.div>

            <motion.h1
              variants={motionFadeUp}
              custom={1}
              className="mt-5 text-5xl sm:text-6xl lg:text-[5rem] font-extrabold leading-[0.92] tracking-tight max-w-4xl"
              style={{ color: C.forest }}
            >
              Privacy, in
              <br />
              <span style={{ color: `${C.forest}80` }}>plain English.</span>
            </motion.h1>

            <motion.p
              variants={motionFadeUp}
              custom={2}
              className="mt-6 text-lg md:text-xl leading-relaxed max-w-2xl"
              style={{ color: C.grid }}
            >
              Short version: we collect what we need to run the service, we don't
              sell your data, and you can ask for a copy or deletion any time.
            </motion.p>
          </motion.div>
        </PageContainer>
      </section>

      {/* SECTIONS */}
      <section className="py-16 md:py-20 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
            {/* Side nav */}
            <aside className="lg:col-span-3">
              <div className="sticky top-28">
                <TechLabel style={{ color: `${C.grid}99` }}>contents</TechLabel>
                <ul className="mt-4 space-y-2.5">
                  {sections.map((s, i) => (
                    <li key={s.title}>
                      <a
                        href={`#sec-${i + 1}`}
                        className="group flex items-start gap-2 text-sm transition-colors hover:text-[#1A3C2B]"
                        style={{ color: C.grid }}
                      >
                        <span
                          className="text-[10px] font-bold tabular-nums mt-0.5"
                          style={{ letterSpacing: "0.18em", color: C.forest }}
                        >
                          0{i + 1}
                        </span>
                        <span className="group-hover:underline">{s.title}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Body */}
            <div className="lg:col-span-9 space-y-12">
              {sections.map((s, i) => (
                <motion.section
                  key={s.title}
                  id={`sec-${i + 1}`}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4 }}
                  className="scroll-mt-28"
                >
                  <div className="flex items-center gap-4 mb-5">
                    <div
                      className="w-11 h-11 flex items-center justify-center flex-shrink-0"
                      style={{ background: C.forest, color: C.paper }}
                    >
                      <s.icon className="w-5 h-5" strokeWidth={1.6} />
                    </div>
                    <div>
                      <TechLabel style={{ color: `${C.grid}99` }}>0{i + 1}</TechLabel>
                      <h2 className="text-2xl md:text-3xl font-bold leading-tight" style={{ color: C.forest }}>
                        {s.title}
                      </h2>
                    </div>
                  </div>
                  <div className="space-y-4 text-base leading-relaxed" style={{ color: C.grid }}>
                    {s.body.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                  </div>
                </motion.section>
              ))}
            </div>
          </div>
        </PageContainer>
      </section>

      {/* CONTACT FOOTER */}
      <section className="py-16 md:py-20" style={{ background: C.forest, color: C.paper }}>
        <PageContainer>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <StatusBadge color={C.mint}>Need anything?</StatusBadge>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold leading-[1.05] tracking-tight">
                Reach our privacy team directly.
              </h2>
              <p className="mt-3 text-base" style={{ color: "rgba(247,247,245,0.7)" }}>
                Real humans, response within one business day.
              </p>
            </div>
            <div className="flex flex-col gap-2 min-w-0">
              <a
                href="mailto:Info@webidealglobal.com"
                className="inline-flex items-center gap-3 px-5 py-3 border transition-colors hover:bg-[#9EFFBF] hover:text-[#1A3C2B]"
                style={{ borderColor: "rgba(158,255,191,0.4)", color: C.paper }}
              >
                <Mail className="w-4 h-4" />
                <span className="text-sm font-semibold">Info@webidealglobal.com</span>
              </a>
              <Link
                to={createPageUrl("Terms")}
                className="text-[10px] font-bold uppercase mt-1 hover:text-[#9EFFBF] transition-colors"
                style={{ letterSpacing: "0.18em", color: "rgba(247,247,245,0.55)" }}
              >
                See also · Terms of Service →
              </Link>
            </div>
          </div>
        </PageContainer>
      </section>
    </div>
  );
}
